/* eslint-disable complexity */
import Cookies from 'js-cookie';
import React, {Fragment, useEffect, useState, useContext} from 'react';
import {BannerV1} from './BannerV1/BannerV1';
import {BannerModal} from './Modal/Modal';
import {getBrand, getStarRatingsBannerCTA, getRedesignCTA, getSiteLocale, RATING_TEXT, appBannerCtaLinks, appModalCtaLinks, appButtonCtaLinks, RATINGS, STARS} from './utils/bannerHelpers';
import {getExperimentValue} from './utils/experiment';
import {I18n, readTransalationsBundles} from './utils/translation';
import {initBranch} from './utils/branch';
import {TrackingContext} from '@homeaway/react-event-tracking';
import {handleHiddenEdapEvent} from './utils/triggerEdapEvent';
import {AppDownloadButton} from './AppDownloadButton/AppDownloadButton';
import {AppDownloadDesktopButton} from './AppDownloadDesktopButton/AppDownloadDesktopButton';

function AppBanner({locale, mobileOS, page}) {
    const renderNothing = () => {
        return (<Fragment />);
    };

    const fullLocale = locale ? locale : 'en_US_VRBO';
    locale = locale ? locale.match(/^.{2}_.{2}/)[0] : 'en_US';
    mobileOS = mobileOS || 'ios';

    const appBrandName = getBrand(fullLocale, locale);
    const travelerApp = appBrandName.toLowerCase();
    const getCtaLinks = (displayType) => {
        switch (displayType) {
            case 'banner' :
                return appBannerCtaLinks[travelerApp][page];
            case 'modal' :
                return appModalCtaLinks[travelerApp][page];
            case 'button':
                return appButtonCtaLinks[travelerApp][page];
            default :
                return '';
        }
    };
    let bannerCtaLink = (travelerApp && page) ? getCtaLinks('banner') : null;
    let modalCtaLink = (travelerApp && page) ? getCtaLinks('modal') : null;
    const appDownloadButtonCtaLink = (travelerApp && page) ? getCtaLinks('button') : null;
    const canonicalUrlPage = ['serp', 'pdp'];
    const useCanonicalPath = page && canonicalUrlPage.indexOf(page.toLowerCase()) > -1 ? true : false;
    const showFullStars = (mobileOS === 'android') ? false : true;
    const siteLocale = locale ? getSiteLocale(locale) : 'en-us';
    let props = {
        travelerApp,
        useCanonicalPath,
        siteLocale,
        showFullStars
    };

    const [headerExpVariant, setHeaderVariant] = useState(0);
    const [headerDesktopVariant, setHeaderDesktopVariant] = useState(0);
    const [switchExpVariant, setSwitchVariant] = useState(1);
    const [labels, setLabels] = useState({});
    const [branchInfo, setBranchInfo] = useState({});
    const [componentType, setComponentType] = useState({});
    const context = useContext(TrackingContext);

    const getLabels = async () => {
        await I18n.load(locale);
        const labelObj = page && page !== 'harness' ? readTransalationsBundles(page, {brand: appBrandName}) : {};
        labelObj.ratings = labelObj.ratings || RATING_TEXT;
        const mobileOsRating = RATINGS[`${mobileOS}`];
        labelObj.rating = `${mobileOsRating} ${labelObj.ratings}`;
        labelObj.stars = STARS[`${mobileOS}`];
        setLabels(labelObj);
    };
    const euSiteCookie = Cookies.get('eu-site');
    const gdprCookie = Cookies.get('ha-cookie-settings'); // 'ha-cookie-settings' is a GDPR cookie used to set the consent for PII by EU user.

    const hasGdprClearance = () => {
        return !(euSiteCookie === '1' && gdprCookie === 'functional:1'); // 'functional:0' is the consent for PII
    };

    const isGDPRBannerHidden = () => {
        return (!euSiteCookie || euSiteCookie === '0' || gdprCookie);
    };

    const getComponentType = () => {
        let componentTypeInfo = 'others';
        switch (page) {
            case 'mytrips':
                componentTypeInfo = 'modal';
                break;
            case 'confirmation':
                componentTypeInfo = 'modal';
                break;
            case 'serp':
                componentTypeInfo = 'banner';
                break;
            case 'homepage':
                componentTypeInfo = 'modal';
                break;
            case 'inbox':
                componentTypeInfo = 'banner';
                break;
            case 'pdp':
                componentTypeInfo = 'banner';
                break;
            case 'header':
                componentTypeInfo = 'button';
                break;
            case 'desktopHeader':
                componentTypeInfo = 'desktopButton';
                break;
            default:
                componentTypeInfo = 'others';
        }
        return componentTypeInfo;
    };

    const setBranchAndComponentTypeInfo = () => {
        const componentTypeInfo = getComponentType();
        setComponentType(componentTypeInfo);
        const hasAppExpVariant = getExperimentValue('HA_NATIVE_ADOPTION_BANNER_USE_HAS_APP');
        const hasAppExcluded = ['others', 'button', 'desktopButton'];
        branchInfo.branchInitializationRequired = (hasAppExcluded.indexOf(componentTypeInfo) === -1 && hasAppExpVariant === 1 && hasGdprClearance());
        setBranchInfo(branchInfo);
    };

    const setComponentTypeAndInitilizeBranchSDK = () => {
        setBranchAndComponentTypeInfo();
        if (branchInfo.branchInitializationRequired && !branchInfo.hasApp) {
            try {
                initBranch((err, data) => {
                    const hasApp = (data && data.has_app) ? 'true' : 'false';
                    branchInfo.hasApp = hasApp;
                    setBranchInfo({...branchInfo, branchInfo});
                }, false);
            } catch (err) {
                branchInfo.hasApp = 'false';
                setBranchInfo({...branchInfo, branchInfo});
            }
        }
    };

    useEffect(() => {
        if (page === 'desktopHeader') {
            const headerDesktopExp = getExperimentValue('Vrbo_Header_Desktop_permanent_MAD_placement');
            setHeaderDesktopVariant(headerDesktopExp);
        }

        const switchExp = getExperimentValue('HA_NATIVE_ADOPTION_PLACEMENTS_MASTER_SWITCH');
        setSwitchVariant(switchExp);
        const headerExp = getExperimentValue('HA_NATIVE_ADOPTION_PLACEMENT_HEADER');
        setHeaderVariant(headerExp);
    }, [page]);

    useEffect(() => {
        setComponentTypeAndInitilizeBranchSDK();
        getLabels();
    }, []);

    const renderBanner = () => {
        const bannerStarExp = getExperimentValue('Vrbo_star_rating_header_banner');
        const showStar = (page !== 'harness' && (page !== 'inbox' && bannerStarExp !== 0)) ? true : false;
        const variantName = `variant_${bannerStarExp}`;
        const bannerVariantCTA = page !== 'inbox' && page !== 'harness' ? getStarRatingsBannerCTA[travelerApp][page][variantName] : bannerCtaLink;
        const starColor = showStar && bannerStarExp === 1 ? true : false;
        // eslint-disable-next-line no-undefined
        if (page === 'harness' || labels.title !== undefined) {
            return (<BannerV1 ctaLink={bannerVariantCTA} {...labels} {...props} showStar={showStar} starColor={starColor}/>);
        }
        return (<Fragment />);
    };

    const renderModal = () => {
        // eslint-disable-next-line no-undefined
        if (page === 'harness' || labels.title !== undefined) {
            return (<BannerModal ctaLink={modalCtaLink} {...labels} {...props}/>);
        }
        return (<Fragment />);
    };

    const renderButtonComponent = () => {
        return (<AppDownloadButton ctaLink={appDownloadButtonCtaLink} {...labels} {...props}/>);
    };

    const renderDesktopHeaderComponent = () => {
        return (<AppDownloadDesktopButton {...labels} {...props}/>);
    };

    const renderMyTrips = () => {
        const bannerExp = getExperimentValue('Vrbo_trip_details_redesign_App_banner');
        const variantName = `variant_${bannerExp}`;
        modalCtaLink = getRedesignCTA[travelerApp][page][variantName];
        switch (bannerExp) {
            case 1:
                labels.description = labels.descriptionV1;
                labels.title = labels.titleV1;
                break;
            default:
        }
        return renderModal();
    };
    const renderInbox = () => {
        const bannerExp = getExperimentValue('Vrbo_trip_details_redesign_App_Modal');
        const variantName = `variant_${bannerExp}`;
        bannerCtaLink = getRedesignCTA[travelerApp][page][variantName];
        switch (bannerExp) {
            case 1:
                labels.description = labels.descriptionV1;
                labels.title = labels.titleV1;
                break;
            default:
        }
        return renderBanner();
    };
    const renderConfirmation = () => {
        return renderModal();
    };
    const renderSerp = () => {
        return (componentType === 'banner' ? renderBanner() : renderNothing());
    };
    const renderPdp = () => {
        return (componentType === 'banner' ? renderBanner() : renderNothing());
    };
    const renderHomePage = () => {
        labels.description = labels.modal_description;
        labels.title = labels.modal_title;
        labels.buttonLabel = labels.modal_buttonLabel;
        return renderModal();
    };
    const renderHeader = () => {
        return (componentType === 'button' ? renderButtonComponent() : renderNothing());
    };
    const renderDesktopHeader = () => {
        return (componentType === 'desktopButton' ? renderDesktopHeaderComponent() : renderNothing());
    };
    const triggerHasAppEdapEvent = () => {
        if (branchInfo.hasApp === 'true') {
            handleHiddenEdapEvent(context, componentType);
        }
    };

    const mobAppInstalled = () => {
        return branchInfo.branchInitializationRequired && (!branchInfo.hasApp || branchInfo.hasApp === 'true');
    };

    const appBanner = () => {
        if (switchExpVariant === 1 && page !== 'harness') {
            return renderNothing();
        }
        if (headerDesktopVariant === 0 && page === 'desktopHeader') {
            return renderNothing();
        }
        if (headerExpVariant === 0 && page === 'header') {
            return renderNothing();
        }
        if (mobAppInstalled()) {
            triggerHasAppEdapEvent();
            return renderNothing();
        }
        if (!isGDPRBannerHidden() && componentType !== 'desktopButton' && componentType !== 'button') {
            return renderNothing();
        }
        switch (page) {
            case 'mytrips':
                return renderMyTrips();
            case 'confirmation':
                return renderConfirmation();
            case 'serp':
                return renderSerp();
            case 'pdp':
                return renderPdp();
            case 'homepage':
                return renderHomePage();
            case 'inbox':
                return renderInbox();
            case 'header':
                return renderHeader();
            case 'desktopHeader':
                return renderDesktopHeader();
            case 'harness':
                return renderBanner();
            default: return renderNothing();
        }
    };
    return appBanner();
}

export default AppBanner;
